import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';

import CreditCardIcon from '@material-ui/icons/CreditCard';
import BankIcon from '@material-ui/icons/AccountBalance';
import CheckIcon from '@material-ui/icons/Receipt';
import MembershipGoodIcon from '@material-ui/icons/VerifiedUser';
import MembershipBadIcon from '@material-ui/icons/Warning';

import ChargePaymentMethodDialog from 'app/components/accounting/payments/ChargePaymentMethodDialog';

import { paper } from 'app/styles/core';
import { containerCenter } from 'app/styles/new-layout';

import useApp from 'app/context/useApp';

const useStyles = makeStyles(theme => ({
    container: {
        ...containerCenter(theme),
        padding: theme.spacing(3),
    },
    paper: {
        ...paper(theme),
        width: '100%',
        marginBottom: theme.spacing(3)
    },
    membershipGoodIcon: {
        width: '30%',
        height: 'auto',
        color: theme.palette.success.main
    },
    membershipBadIcon: {
        width: '30%',
        height: 'auto',
        color: theme.palette.error.main,
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(3),
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    textCenter: {
        textAlign: 'center'
    },
    textSuccess: {
        color: theme.palette.success.main
    },
    checkAddress: {
        padding: theme.spacing(2)
    },
    invoice: {
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3)
    },
}));

const PaymentChip = props => {

    const { pm } = props;
    const classes = useStyles();

    const getIcon = () => {
        switch (pm) {
            case 'stripe': return <CreditCardIcon/>;
            case 'stripe_ach': return <BankIcon/>;
            case 'check': return <CheckIcon/>;
        }
    }

    const getLabel = () => {
        switch (pm) {
            case 'stripe': return 'Credit Card';
            case 'stripe_ach': return 'ACH Debits';
            case 'check': return 'Check';
        }
    }

    return (
        <Chip
            key={pm}
            icon={getIcon()}
            label={getLabel()}
            color="secondary"
            variant="outlined"
            className={classes.marginRight}
        />
    )
}

export default function Membership() {

    const { app, hydrateApp } = useApp();
    const classes = useStyles();

    const membership = app.user.membership_status;

    const [ activeInvoice, setActiveInvoice ] = useState(null);
    const [ chargePaymentMethodDialogOpen, setChargePaymentMethodDialogOpen ] = useState(false);

    const openChargePaymentMethodDialog = (invoice) => {
        setActiveInvoice(invoice);
        setChargePaymentMethodDialogOpen(true);
    }

    const closeChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(false);
    }

    const closeChargePaymentMethodDialogAndRefresh = () => {
        hydrateApp(true);
        setActiveInvoice(null);
        closeChargePaymentMethodDialog();
    }

    const membershipIcon = () => {
        if (membership.in_good_standing) {
            return (
                <div className={classes.textCenter}>
                    <MembershipGoodIcon className={classes.membershipGoodIcon}/>
                    <Typography variant="subtitle2" color="textSecondary" component="h3">
                        Your membership is in
                        <Typography
                            component="span"
                            variant="subtitle2"
                            className={classes.textSuccess}
                            display="inline"
                            >
                            &nbsp;good&nbsp;
                        </Typography>
                        standing
                    </Typography>
                </div>
            )
        } else {
            return (
                <div className={classes.textCenter}>
                    <MembershipBadIcon className={classes.membershipBadIcon}/>
                    <Typography variant="subtitle2" color="textSecondary" component="h3">
                        Your membership is in
                        <Typography
                            component="span"
                            variant="subtitle2"
                            color="error"
                            display="inline"
                            >
                            &nbsp;bad&nbsp;
                        </Typography>
                        standing
                    </Typography>
                </div>
            )
        }
    }

    const membershipContent = () => {
        if (membership && membership.is_membership_paid && membership.in_good_standing) {
            return (
                <>
                    <Typography variant="body1" color="textSecondary">
                        Your membership is in good standing. {membership.renews_on ? "Memberships renew on " + membership.renews_on + "." : "You have no upcoming membership dues at this time so feel free to sit back and enjoy all your organization has to offer." }
                        {membership.due_on &&
                            <>
                                <br/><br/>
                                Fees paid after {membership.due_on} are considered late and may have an additional late fee applied. Additionally, {app.club.members_group.title} in bad standing may not have access to events and other functions and features.
                            </>
                        }
                    </Typography>
                </>
            )
        } else if (membership && membership.in_good_standing && !membership.is_membership_paid) {
            return (
                <>
                    <Typography variant="body1">
                        Your membership is due for payment at this time. You may be charged an additional late fee if you have not paid in full on or before
                        <Typography
                            variant="body1"
                            color="error"
                            display="inline"
                            component="span"
                            >
                            &nbsp;{membership.due_on}
                        </Typography>. Additionally, {app.club.members_group.title} in bad standing may not have access to events and other functions and features.
                    </Typography>
                    <br/>
                    <Typography variant="body1">
                        You may pay for your membership using the payment options listed below.
                    </Typography>
                </>
            )
        } else {
            return (
                <>
                    <Typography variant="subtitle2" gutterBottom>
                        Payment for your membership was due on or before
                        <Typography variant="subtitle2" color="error" display="inline" component="span" >
                            &nbsp;{membership.due_on}&nbsp;
                        </Typography>
                        and is now late. You may pay for your membership using the payment options listed below.
                        <br/>
                        Additionally, {app.club.members_group.title} in bad standing may not have access to events and other functions and features.
                    </Typography>
                </>
            )
        }
    };

    const onlinePaymentEnabled = (app.club.meta.payment_methods.includes('stripe_ach') || app.club.meta.payment_methods.includes('stripe'));

    return (
        <div className={classes.container}>
            <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h4" component="h2" gutterBottom>
                        Your Membership
                    </Typography>
                    <Divider/>
                </Grid>
                <Grid item xs={12} md={6}>
                    {membershipIcon()}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper className={classes.paper}>
                        <Grid
                            container
                            alignItems="center"
                            justify="space-between"
                            spacing={3}
                            >
                            <Grid item>
                                <Typography variant="h6" component="h3">
                                    Membership Details
                                </Typography>
                            </Grid>
                        </Grid>
                        <Typography variant="subtitle2" color="secondary">
                            {app.user.membership_title}
                        </Typography>
                        <br/>
                        {membershipContent()}
                    </Paper>
                    <Paper className={classes.paper}>
                        <Grid
                            container
                            alignItems="center"
                            justify="space-between"
                            spacing={3}
                            >
                            <Grid item>
                                <Typography variant="h6" component="h3">
                                    Membership Dues
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="caption">
                                    *Online payments may include an additional <strong>Convenience Fee</strong>
                                </Typography>
                            </Grid>
                        </Grid>
                        {(membership && (membership.invoices.length > 0) && !membership.is_membership_paid) ? (
                            <div className={classes.marginTop}>
                                {membership.invoices.map(invoice => (
                                    <Grid container alignItems='center' justify='space-between' spacing={1} key={invoice.id} className={classes.invoice}>
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                {invoice.description}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" color="primary">
                                                ${invoice.amount_f}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" color={DateTime.fromISO(invoice.due_by) < DateTime.local() ?"error" : "textSecondary"}>
                                                Due&nbsp;{DateTime.fromISO(invoice.due_by).toLocaleString(DateTime.DATE_MED)}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Tooltip
                                                title={invoice.processing ? 'Your payment for this invoice is pending. We will email you once the status of this payment changes.' : onlinePaymentEnabled ? 'Pay Invoice' : `Online Payments disabled for ${app.club.name}`}
                                                aria-label="pay"
                                                >
                                                <span>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => openChargePaymentMethodDialog(invoice)}
                                                        variant="outlined"
                                                        disabled={(!onlinePaymentEnabled || invoice.processing)}
                                                        >
                                                        {invoice.processing ? "PENDING" : 'PAY'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                ))}
                                {app.club.meta.payment_methods.includes('check') &&
                                    <>
                                        <Typography color="textSecondary" variant="subtitle2">
                                            To pay by check, please send payment to the following address:
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            color="textSecondary"
                                            component="div"
                                            className={classes.checkAddress}
                                            >
                                            {app.club.meta.check_name}<br/>
                                            {app.club.meta.check_address_one} {app.club.meta.check_address_two ?? ''}<br/>
                                            {app.club.meta.check_city}, {app.club.meta.check_state} {app.club.meta.check_zip}
                                        </Typography>
                                    </>
                                }
                                {app.club.meta.payment_methods.includes('stripe_ach') &&
                                    <Typography color="textSecondary" variant="subtitle2" className={classes.marginTop}>
                                        To pay by ACH Debit, please connect to your bank account by adding a new payment method on your profile payment page. <Link to="/profile/payment" component={NavLink} color="secondary">Click Here</Link> to go to your profile payment page.
                                    </Typography>
                                }
                                {(app.club.meta.payment_methods.length === 0) &&
                                    <Typography variant="body1" color="textSecondary" className={classes.marginTop}>
                                        {app.club.name} has not set up any payment methods. Please contact a club administrator at <a href={`mailto:${app.club.support_email}`}>{app.club.support_email}</a> to find out how to submit payments.
                                    </Typography>
                                }
                            </div>
                        ) : (
                            <>
                                <Typography variant="subtitle1" color="textSecondary" className={classes.marginTop}>
                                    You have no membership dues to pay at this time.
                                </Typography>
                                {app.club.meta.payment_methods.length > 0 ? (
                                    <div className={classes.marginTop}>
                                        <Typography
                                            variant="body1"
                                            color="textSecondary"
                                            gutterBottom
                                            >
                                            We accept the following payment methods:
                                        </Typography>
                                        <div>
                                            {app.club.meta.payment_methods.map(pm => <PaymentChip pm={pm}/>)}
                                        </div>
                                    </div>
                                ) : (
                                    <Typography variant="body1" color="textSecondary" className={classes.marginTop}>
                                        {app.club.name} has not set up any payment methods. Please contact a club administrator at <Link color="secondary" href={`mailto:${app.club.support_email}`}>{app.club.support_email}</Link> to find out how to submit payments.
                                    </Typography>
                                )}
                                <Typography color="textSecondary" variant="subtitle2" className={classes.marginTop}>
                                    You can save payment methods at any time to help expedite future payments. <Link component={NavLink} color="secondary" to="/profile/payment">Click Here</Link> to go to your profile payment page and save a new payment method.
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            <ChargePaymentMethodDialog
                user={app.user}
                club={app.club}
                invoice={activeInvoice}
                urlPrefix={'/profile'}
                open={chargePaymentMethodDialogOpen}
                onClose={closeChargePaymentMethodDialog}
                onCharge={closeChargePaymentMethodDialogAndRefresh}
            />
        </div>
    )
}
